import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Calendar, Share2 } from 'lucide-react';
import ShareEngagementModal from '../share/ShareEngagementModal';
import InviteDecisionPanel from '../invite/InviteDecisionPanel';
import { eventNameFor } from '../../../utils/eventName';
import TalkPanel, { BLUE } from './TalkPanel';
import * as SelfApi from '../selfApi';

const API = process.env.REACT_APP_API_URL || '';

// The speaker area of the profile ("Referentenbereich"): what used to be the separate Speaker Hub
// page (/speaker/dashboard) below its hero, without any page chrome of its own - AttendeeProfile.js
// gives it its section heading and its place in the grid. /speaker/dashboard only redirects to
// /profile#referentenbereich now, so the links already sent in invitation and reminder emails land
// here. Open invitations are answered here too, inline (since 2026-10-20; before, they were a
// separate list in the profile's right column).

// Cards: the profile's own history card (AttendeeProfile.js RegCard, RoleTag, StatusTag), so the
// speaker area and the history next to it read as one system. An open invitation carries the brand
// blue on its left edge - the one kind of card on the page that waits for the person.
const SpotCard = styled.div`display:flex;flex-direction:column;background:#fff;padding:.9rem;margin-bottom:.8rem;box-shadow:0 6px 16px rgba(15,23,42,.06);scroll-margin-top:1rem;${p => (p.$open ? `border-left:4px solid ${BLUE};padding-left:calc(.9rem - 4px);` : '')}${p => (p.$highlighted ? `outline:2px solid ${BLUE};outline-offset:2px;` : '')}`;
const CardTop = styled.div`display:flex;gap:1rem;align-items:center;@media(max-width:420px){gap:.75rem;}`;
const Thumb = styled.img`width:112px;height:82px;object-fit:cover;flex:none;background:#eef2f7;@media(max-width:420px){width:84px;height:62px;}`;
const ThumbFallback = styled.div`width:112px;height:82px;flex:none;background:#eef2f7;display:flex;align-items:center;justify-content:center;color:#94a3b8;@media(max-width:420px){width:84px;height:62px;}`;
const CardInfo = styled.div`min-width:0;`;
const CardName = styled.div`font-weight:700;color:#111D4E;font-size:1.02rem;`;
const CardMeta = styled.div`color:#5B6B78;font-size:.85rem;margin-top:.12rem;`;
const RoleTag = styled.span`display:inline-block;padding:.1rem .5rem;font-size:.68rem;font-weight:800;letter-spacing:.03em;text-transform:uppercase;background:#111D4E;color:#fff;margin-right:.4rem;vertical-align:.08em;`;
const StatusTag = styled.span`display:inline-block;margin-top:.35rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:${p => (p.$confirmed ? '#dcfce7' : '#e0f2fe')};color:${p => (p.$confirmed ? '#166534' : '#075985')};`;
const PastTag = styled.span`display:inline-block;margin-top:.35rem;margin-left:.4rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:#e5e7eb;color:#6b7280;`;
// The answer itself, inline - the profile's former InviteBox: a hairline under the thumbnail row.
const InviteBox = styled.div`margin-top:.75rem;padding-top:.75rem;border-top:1px solid #eef2f7;`;
// The groups of the section (waiting for an answer, confirmed): the profile's motivation label
// (small caps over the brand gradient line) with an italic line under it.
const Group = styled.div`& + &{margin-top:1.6rem;}`;
const GroupHead = styled.div`margin:0 0 .8rem;`;
const GroupLabel = styled.h3`
  display:inline-block;margin:0;font-size:.68rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#5B6B78;
  padding-bottom:6px;background-image:linear-gradient(to right,#4e4094,#14cbde);background-size:100% 2px;background-position:0 bottom;background-repeat:no-repeat;
`;
const GroupNote = styled.div`color:#5B5B5B;font-style:italic;font-size:.85rem;margin-top:.35rem;`;
// What the person just answered, shown where the spot went: a confirmation on the confirmed card, a
// thank-you where the invitation card was (a declined spot moves to the history).
const Success = styled.div`background:#d1fae5;color:#065f46;padding:.6rem .8rem;margin-top:.7rem;font-size:.86rem;line-height:1.5;`;
const Thanks = styled.div`background:#fff;border-left:4px solid #cbd5e1;padding:.75rem .9rem;margin-bottom:.8rem;color:#334155;font-size:.88rem;line-height:1.5;box-shadow:0 6px 16px rgba(15,23,42,.06);`;
const ThanksTitle = styled.div`font-weight:700;color:#111D4E;margin-bottom:.2rem;`;
const Empty = styled.p`color:#5B6B78;`;

// Sits below the role/status tags on a confirmed engagement - any confirmed
// role (speaker, moderator, panelist) can generate a share image, not just
// SPEAKER rows (those alone get the TalkPanel below).
const ShareRow = styled.div`margin-top:.5rem;`;
const ShareButton = styled.button`display:inline-flex;align-items:center;gap:.35rem;border:1px solid #cbd5e1;background:#fff;color:#334155;padding:.35rem .7rem;font-size:.78rem;font-weight:600;cursor:pointer;transition:border-color .15s,color .15s;&:hover{border-color:${BLUE};color:${BLUE};}`;

// Event thumbnail with graceful fallback (missing URL or image that fails to load).
const EventThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <ThumbFallback><Calendar size={22} /></ThumbFallback>;
  return <Thumb src={`${API}${url}`} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
};

// Purely about speaking engagements (talk title, slides) - name, bio, specialty, photo, interests etc.
// are the profile's own, around this section.
//
// The one place on the profile for everything about speaking (owner, 2026-10-20). The cards are the
// person's spots (D10), handed in by the profile, which reads GET /api/me/invitations for its history
// anyway (and reads it again after an answer, through onAnswered). Two groups:
//   - "Your invitations": every spot still waiting for an answer, answered right on its card
//     (InviteDecisionPanel, that one spot - its per-spot rules, 409 handling and texts are its own);
//   - "Confirmed": every confirmed spot, with the share button, and for a SPEAKER spot the talk editor
//     for its entry (GET /api/me/talks, read here).
// Declined, withdrawn and cancelled spots need nothing more and are the profile's history rows only
// (see inSpeakerArea); a confirmed spot of a past event is both: managed here (the slides and the
// consent still matter after the talk), recorded in the history.
const statusFor = (spotState) => (spotState === 'OPEN' ? 'INVITED' : spotState);
const isPast = (dateTime) => Boolean(dateTime) && new Date(dateTime) < new Date();

// Which spots this section shows: the ones the person still answers or manages.
export const inSpeakerArea = (spot) => spot.state === 'OPEN' || spot.state === 'CONFIRMED';

// The cards of the person's spots, newest first, as the old hub listed its engagement rows (the
// spots of one invitation share its time: the later one first).
const rowsFrom = (invitations, talks) => {
  const list = [];
  (invitations || []).forEach((group) => {
    (group.spots || []).filter(inSpeakerArea).forEach((spot) => {
      const isTalk = spot.role === 'SPEAKER';
      list.push({
        key: `${group.eventId}-${spot.assignmentId}`,
        assignmentId: spot.assignmentId,
        eventId: group.eventId,
        eventSlug: group.event?.slug,
        eventName: group.event?.name,
        eventNameEN: group.event?.nameEn,
        eventDateTime: group.event?.dateTime,
        eventLocation: group.event?.location,
        eventImageUrl: group.event?.imageUrl,
        role: spot.role,
        status: statusFor(spot.state),
        // The old card meta: the session with its time, and a talk's own time ("Your talk: ...")
        // when it has one of its own; a chair's or panelist's spot is the session itself.
        sessionTitle: isTalk ? spot.blockTitle : (spot.entryTitle || spot.blockTitle),
        sessionTime: isTalk ? (spot.blockTitle ? spot.blockTime : null) : spot.time,
        talkTime: isTalk && spot.time && spot.time !== spot.blockTime ? spot.time : null,
        entryDate: spot.date || null,
        entryTime: spot.time || '',
        // The talk row of GET /api/me/talks for this spot: the entry the person's materials (or, for
        // an entry that asks for no slides, the title) belong to. The service answers one for every
        // confirmed SPEAKER assignment, so a confirmed speaker spot without slides still has one.
        talk: Array.isArray(talks) ? (talks.find((row) => row.assignmentId === spot.assignmentId) || null) : null,
        sortAt: String(spot.askedAt || '').slice(0, 19),
        sortTie: spot.invitationId || 0,
      });
    });
  });
  return list.sort((a, b) => b.sortAt.localeCompare(a.sortAt) || b.sortTie - a.sortTie);
};

// The order of one event's confirmed spots in the share preview's picker: talks first (the share
// reminder is about them), then by day and time in the program, then the spot.
const byProgramSlot = (a, b) => Number(a.role !== 'SPEAKER') - Number(b.role !== 'SPEAKER')
  || String(a.entryDate || '9999').localeCompare(String(b.entryDate || '9999'))
  || String(a.entryTime || '99').localeCompare(String(b.entryTime || '99'))
  || a.assignmentId - b.assignmentId;

// The person's confirmed spots at one event, in picker order; the first is the one a share link opens.
export const shareChoices = (rows, eventId) => rows
  .filter((row) => row.status === 'CONFIRMED' && String(row.eventId) === String(eventId))
  .sort(byProgramSlot);

// What the share preview needs of a card row.
const toEngagement = (row) => ({
  ...row,
  id: row.key,
  talkTitle: row.talk ? (row.talk.titleEn || row.talk.titleDe) : undefined,
  talkTitleSuggestion: row.talk ? row.talk.titleSuggestion : undefined,
  // the slot the card prints: the talk's own time, else the session's
  sessionStartTime: (row.sessionTime || '').split('–')[0] || undefined,
  sessionEndTime: (row.sessionTime || '').split('–')[1] || undefined,
  talkStartTime: (row.talkTime || '').split('–')[0] || undefined,
  talkEndTime: (row.talkTime || '').split('–')[1] || undefined,
});

// Whether the profile shows this section at all: only for a person who was asked to a spot (any
// state) - a plain attendee never sees an empty speaker area.
export const hasSpeakerSpots = (invitations) => (invitations || []).some((group) => (group.spots || []).length > 0);

// `onAnswered`: the profile reads its lists again after an answer. `highlightEventId`: the event of
// the ?invite= link the profile was opened with - its cards are outlined. `registerCard(eventId, el)`:
// hands the profile each card, so it can scroll to the event's first one (the invitation, while open).
// `shareEventId`: the event of the ?share= link (the share reminder's button) - its share preview opens
// right away, on the first of the person's confirmed spots there (shareChoices); with none, nothing opens.
const SpeakerArea = ({ invitations, identity, shareUrl = null, onAnswered, highlightEventId, registerCard, shareEventId = null }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  // null while loading, { error: true } when the read failed
  const [talks, setTalks] = useState(null);
  // The row key of the card whose share preview is open, not the row itself: the
  // row is looked up on every render (see `shareEngagement` below), so a preview
  // opened before GET /api/me/talks answers still picks up its talk title/time
  // when the read lands.
  // The profile mounts this section only once the invitations have loaded, so a share link's spot is
  // known here already.
  const [shareKey, setShareKey] = useState(() => (shareEventId
    ? shareChoices(rowsFrom(invitations, null), shareEventId)[0]?.key || null
    : null));
  // What the person answered here, per assignment: {action: 'accepted' | 'declined', suggested} -
  // shown where the spot went once the profile has read the invitations again.
  const [answered, setAnswered] = useState({});

  // Read again whenever the invitations change: a spot just accepted is a confirmed SPEAKER
  // assignment now, with a talk row of its own.
  useEffect(() => {
    let cancelled = false;
    SelfApi.fetchTalks()
      .then((view) => { if (!cancelled) setTalks((view && view.talks) || []); })
      .catch(() => { if (!cancelled) setTalks({ error: true }); });
    return () => { cancelled = true; };
  }, [invitations]);

  const updateTalk = (talk) => {
    setTalks((current) => (Array.isArray(current) ? current : []).map((row) => (
      row.assignmentId === talk.assignmentId ? talk : row
    )));
  };

  // A 409 on an order action means another speaker changed the files in between: the list is read
  // again, so every card shows the state the server has now.
  const reloadTalks = () => SelfApi.fetchTalks()
    .then((view) => setTalks((view && view.talks) || []))
    .catch(() => {});

  const onResolved = (answer) => {
    if (answer?.assignmentId) {
      setAnswered((current) => ({ ...current, [answer.assignmentId]: { action: answer.action, suggested: answer.suggested } }));
    }
    onAnswered?.();
  };

  const statusLabel = s => t(`speaker.engagement.${s}`, s);
  const roleLabel = r => t(`profile.history.role.${r}`, r);
  const formatDate = v => v ? new Date(v).toLocaleString(intl.locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const sessionLabel = item => [
    item.sessionTitle,
    item.sessionTime,
    item.talkTime && t('speaker.dashboard.talkTime', 'Your talk: {time}', { time: item.talkTime }),
  ].filter(Boolean).join(' · ');

  const rows = rowsFrom(invitations, talks);
  const openRows = rows.filter((row) => row.status === 'INVITED');
  // upcoming events first, then the past ones; within each, newest first as above
  const confirmedRows = rows.filter((row) => row.status === 'CONFIRMED')
    .sort((a, b) => Number(isPast(a.eventDateTime)) - Number(isPast(b.eventDateTime)));
  // The open preview, rebuilt from the current rows rather than snapshotted when
  // the button was clicked - the talks read may still be on its way.
  const shareEngagement = shareKey ? confirmedRows.find((row) => row.key === shareKey) : null;
  const shareOptions = shareEngagement ? shareChoices(confirmedRows, shareEngagement.eventId) : [];
  // Spots declined here a moment ago: their card is gone (the spot is a history row now), the
  // thank-you stays where it was.
  const stillOpen = new Set(openRows.map((row) => row.assignmentId));
  const declinedHere = Object.entries(answered)
    .filter(([id, answer]) => answer.action === 'declined' && !stillOpen.has(Number(id)));
  const highlighted = (item) => Boolean(highlightEventId) && String(item.eventId) === String(highlightEventId);

  const cardHead = (item, meta) => (
    <CardTop>
      <EventThumb url={item.eventImageUrl} />
      <CardInfo>
        <CardName>
          <RoleTag>{roleLabel(item.role)}</RoleTag>
          {eventNameFor(intl.locale, item.eventName, item.eventNameEN)}
        </CardName>
        <CardMeta>{meta}</CardMeta>
        <StatusTag $confirmed={item.status === 'CONFIRMED'}>{statusLabel(item.status)}</StatusTag>
        {isPast(item.eventDateTime) && <PastTag>{t('profile.history.past', 'Past')}</PastTag>}
      </CardInfo>
    </CardTop>
  );
  const cardRef = (item) => (el) => { if (el) registerCard?.(item.eventId, el); };

  return (
    <>
      {declinedHere.map(([id, answer]) => (
        <Thanks key={`thanks-${id}`} role="status">
          <ThanksTitle>{t('speaker.respond.declined.title', 'Thank you for letting us know')}</ThanksTitle>
          {answer.suggested
            ? t('speaker.respond.declined.withSuggestion', 'We have passed your answer and your suggestion on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')
            : t('speaker.respond.declined.body', 'We have passed your answer on to the organizers. We are sorry you can\'t join us this time and hope to welcome you at a future event.')}
        </Thanks>
      ))}

      {openRows.length > 0 && (
        <Group data-testid="speaker-area-open">
          <GroupHead>
            <GroupLabel>{t('profile.invitations.title', 'Your invitations')}</GroupLabel>
            <GroupNote>{openRows.length > 1
              ? t('speaker.respond.eachSpot', 'Answer each spot on its own – accepting one does not answer the others.')
              : t('profile.invitations.subtitle', 'Answer each spot you were asked about')}</GroupNote>
          </GroupHead>
          {openRows.map((item) => (
            <SpotCard key={item.key} $open $highlighted={highlighted(item)} ref={cardRef(item)}>
              {/* The panel names the session, the talk and its time itself. */}
              {cardHead(item, [formatDate(item.eventDateTime), item.eventLocation].filter(Boolean).join(' · '))}
              <InviteBox>
                <InviteDecisionPanel eventId={item.eventId} assignmentId={item.assignmentId} compact onResolved={onResolved} />
              </InviteBox>
            </SpotCard>
          ))}
        </Group>
      )}

      {confirmedRows.length > 0 && (
        <Group data-testid="speaker-area-confirmed">
          <GroupHead>
            <GroupLabel>{t('profile.speakerArea.confirmed', 'Your confirmed engagements')}</GroupLabel>
          </GroupHead>
          {talks?.error && <Empty>{t('speaker.dashboard.loadError', 'Your speaking engagements could not be loaded.')}</Empty>}
          {confirmedRows.map((item) => (
            <SpotCard key={item.key} $highlighted={highlighted(item)} ref={cardRef(item)}>
              {/* Session detail and the venue are joined, not either/or: a session-titled row used to
                  silently drop the venue. A session-less row filters down to just the venue. */}
              {cardHead(item, [formatDate(item.eventDateTime), sessionLabel(item), item.eventLocation].filter(Boolean).join(' · '))}
              {answered[item.assignmentId]?.action === 'accepted' && (
                <Success role="status">{t('speaker.respond.accepted.body', "You're confirmed - we look forward to welcoming you.")}</Success>
              )}
              {/* Any confirmed role can share the engagement, not just SPEAKER - a moderator/panelist
                  still has a name/photo/slot worth posting, even with no talk of their own. */}
              {identity && (
                <ShareRow>
                  <ShareButton type="button" onClick={() => setShareKey(item.key)}>
                    <Share2 size={13} />{t('speaker.share.button', 'Share this engagement')}
                  </ShareButton>
                </ShareRow>
              )}
              {/* Only SPEAKER engagements have a talk/slides to manage - a moderator/panelist row has
                  nothing else to add here. */}
              {item.role === 'SPEAKER' && item.talk && (
                <TalkPanel
                  talk={item.talk}
                  viewerName={identity?.name}
                  onUpdated={updateTalk}
                  onReload={reloadTalks}
                  defaultOpen={answered[item.assignmentId]?.action === 'accepted'}
                />
              )}
            </SpotCard>
          ))}
        </Group>
      )}

      {openRows.length === 0 && confirmedRows.length === 0 && (
        <Empty>
          <FormattedMessage id="speaker.dashboard.none" defaultMessage="You have no upcoming speaking engagements right now." />
          {' '}{t('profile.speakerArea.answersInHistory', 'Your earlier answers are listed under "HEARTS Conferences".')}
        </Empty>
      )}

      {shareEngagement && identity && (
        <ShareEngagementModal
          engagement={toEngagement(shareEngagement)}
          choices={shareOptions.map(toEngagement)}
          onChoose={setShareKey}
          identity={identity}
          shareUrl={shareEngagement.eventSlug
            ? new URL(`/events/${shareEngagement.eventSlug}`, window.location.origin).href
            : shareUrl}
          ownProfile
          onClose={() => setShareKey(null)}
        />
      )}
    </>
  );
};

export default SpeakerArea;
